import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { HVAC_SERVICES, HVAC_TIME_WINDOWS } from "../../data/leesServices";
import { LEES_CONTACT } from "../../data/leesContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", phone: "", email: "", address: "",
  cleaning_type: "hvac_ac_repair",
  preferred_date: "", preferred_time_window: "",
  emergency: false, message: "",
};

const inputCls = "bg-black border-[#2a2a2a] text-white";

function Field({ id, label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label htmlFor={id} className="text-white">{label}</Label>
      {children}
    </div>
  );
}

export default function HVACQuoteForm({ defaultService }) {
  const [form, setForm] = useState({ ...EMPTY, cleaning_type: defaultService || EMPTY.cleaning_type });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, service_category: "hvac", preferred_contact: "phone" });
      toast.success("Request received — we'll be in touch shortly.");
      setForm({ ...EMPTY, cleaning_type: defaultService || EMPTY.cleaning_type });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not submit.";
      toast.error(`Could not submit: ${detail}. Please call ${LEES_CONTACT.phone}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="bg-[#111111] text-white p-6 md:p-8 border-2 border-[#8B1E1E]" data-testid="hvac-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field id="h-name" label="Full Name *">
          <Input id="h-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="hvac-name" required />
        </Field>
        <Field id="h-phone" label="Phone *">
          <Input id="h-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="hvac-phone" required />
        </Field>
        <Field id="h-email" label="Email">
          <Input id="h-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="hvac-email" />
        </Field>
        <Field id="h-address" label="Address / City">
          <Input id="h-address" value={form.address} onChange={(e) => update("address", e.target.value)} className={inputCls} data-testid="hvac-address" placeholder="e.g. Hobart, IN" />
        </Field>
        <Field id="h-service" label="Service *">
          <Select value={form.cleaning_type} onValueChange={(v) => update("cleaning_type", v)}>
            <SelectTrigger id="h-service" className={inputCls} data-testid="hvac-service-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {HVAC_SERVICES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              <SelectItem value="hvac_other">Other / Not sure</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field id="h-date" label="Preferred Date">
          <Input id="h-date" type="date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className={inputCls} data-testid="hvac-preferred-date" />
        </Field>
        <Field id="h-window" label="Preferred Time Window" wide>
          <Select value={form.preferred_time_window} onValueChange={(v) => update("preferred_time_window", v)}>
            <SelectTrigger id="h-window" className={`${inputCls} data-[placeholder]:text-slate-400`} data-testid="hvac-preferred-window"><SelectValue placeholder="Choose a window" /></SelectTrigger>
            <SelectContent>
              {HVAC_TIME_WINDOWS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field id="h-msg" label="Describe the Issue" wide>
          <Textarea id="h-msg" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className={inputCls} data-testid="hvac-message" placeholder="What's happening with your unit? When did it start?" />
        </Field>
      </div>

      <label className="mt-5 flex items-start gap-3 cursor-pointer p-4 border border-[#8B1E1E] bg-[#1a0a0a]" data-testid="hvac-emergency-row">
        <Checkbox checked={form.emergency} onCheckedChange={(v) => update("emergency", !!v)} className="mt-0.5 border-white data-[state=checked]:bg-[#8B1E1E]" data-testid="hvac-emergency" />
        <span className="text-sm text-white">
          <strong className="hvac-stencil uppercase tracking-wide text-[#FFB4B4]">This is an emergency</strong>
          <span className="block text-slate-400 text-xs mt-1">Flag this so we know to respond immediately.</span>
        </span>
      </label>

      <button type="submit" disabled={submitting} className="w-full mt-6 bg-[#8B1E1E] hover:bg-[#A6271F] text-white font-bold py-4 px-6 uppercase tracking-wider border border-[#8B1E1E] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors" data-testid="hvac-submit">
        {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        Request a Free Quote
      </button>
      <p className="text-xs text-slate-400 mt-4 text-center">
        Or call us directly at{" "}
        <a href={LEES_CONTACT.phoneHref} className="text-[#FFB4B4] underline hover:text-white">{LEES_CONTACT.phone}</a>.
      </p>
    </form>
  );
}
